// RacersList.js
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, query, onSnapshot, doc, deleteDoc, updateDoc, addDoc, arrayRemove, arrayUnion, getDocs, getDoc, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import { FaTrash, FaPencilAlt, FaTimes, FaSortUp, FaSortDown, FaShareAlt } from "react-icons/fa";
import creditCosts from "../config/creditCosts";
import "../App.css";
import ShareGroupModal from "./ShareGroupModal";
import { getShareToken } from "../services/sharing";
import { getSharingGroupByToken, calculateSharingGroupScope } from "../services/sharingGroups";
import { signInAnonymously } from "firebase/auth";
import GroupFilterDropdown from "./GroupFilterDropdown";
import { loadRacerGroupFilters, saveRacerGroupFilters } from "../utils/persistentFilters";

const MAX_IN_QUERY = 10;

const chunkIds = (ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_IN_QUERY) {
    chunks.push(ids.slice(i, i + MAX_IN_QUERY));
  }
  return chunks;
};

const RacersList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const adminUID = "lXK7NXhBv4NpeJiNMPLp2cXNHou1";
  const isAdmin = user && user.uid === adminUID;
  const [racers, setRacers] = useState([]);
  const [events, setEvents] = useState([]);
  const [sharedRuns, setSharedRuns] = useState([]);
  const [newRacerName, setNewRacerName] = useState("");
  const [racerSortType, setRacerSortType] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [editingRacerId, setEditingRacerId] = useState(null);
  const [renameInput, setRenameInput] = useState("");
  const [showGroupInput, setShowGroupInput] = useState({});
  const [selectedGroupFilters, setSelectedGroupFilters] = useState([]); // Array of group names
  const [racerSearchQuery, setRacerSearchQuery] = useState("");
  const [userData, setUserData] = useState(null);
  const [selectedRacers, setSelectedRacers] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);
  const [mergedRacerName, setMergedRacerName] = useState("");
  const [mergedRacerGroups, setMergedRacerGroups] = useState("");
  const [filtersRestored, setFiltersRestored] = useState(false);
  const [groupName, setGroupName] = useState("");

  const shareGroupId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("id");
    if (value) {
      localStorage.setItem("guestShareGroupId", value);
      return value;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    return cached || "";
  }, [location.search]);

  const shareToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const shareGroupToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("shareGroupToken") || "";
  }, [location.search]);

  const [tokenData, setTokenData] = useState(null);
  const [tokenScope, setTokenScope] = useState(null);
  const [shareGroupData, setShareGroupData] = useState(null);
  const [shareGroupScope, setShareGroupScope] = useState(null);

  const isGuest = Boolean(user?.isAnonymous);
  const isGuestShare = Boolean(shareGroupId) && (isGuest || !user);
  const isTokenShare = Boolean(shareToken);
  const isShareGroupShare = Boolean(shareGroupToken);

  const requireAccount = () => {
    if (!user || user.isAnonymous) {
      showToast("Please sign in to edit or create content.");
      navigate("/login");
      return false;
    }
    return true;
  };

  const getFiltersStorageKey = () => {
    if (!user?.uid) return "racersListFilters:anonymous";
    return `racersListFilters:${user.uid}`;
  };

  // Note: Racer group filters now use shared persistentFilters utility
  // This getFiltersStorageKey is still used for sort preferences

  const trackParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("track");
    return value ? value.trim() : "";
  }, [location.search]);

  const normalizedTrackParam = useMemo(() => {
    return trackParam.toLowerCase();
  }, [trackParam]);

  // Fetch user data for credits
  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if ((!shareGroupId && !shareToken && !shareGroupToken) || user) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, shareToken, shareGroupToken, user]);

  // Load token data when token is present
  useEffect(() => {
    if (!shareToken) {
      setTokenData(null);
      setTokenScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareToken) {
      // Sign in anonymously if needed
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadToken = async () => {
      try {
        const data = await getShareToken(shareToken);
        if (data && !data.revoked) {
          setTokenData(data);
          setTokenScope(data.scope || { runs: [], racers: [], tracks: [], sessions: [], groups: [] });
        } else {
          setTokenData(null);
          setTokenScope(null);
        }
      } catch (error) {
        console.error("Error loading share token:", error);
        setTokenData(null);
        setTokenScope(null);
      }
    };

    loadToken();
  }, [shareToken, user]);

  // Load sharing group data when shareGroupToken is present
  useEffect(() => {
    if (!shareGroupToken) {
      setShareGroupData(null);
      setShareGroupScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareGroupToken) {
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadSharingGroup = async () => {
      try {
        const groupData = await getSharingGroupByToken(shareGroupToken);
        if (groupData) {
          setShareGroupData(groupData);
          // Calculate scope for the sharing group
          const scope = await calculateSharingGroupScope(groupData.id);
          setShareGroupScope(scope);
        } else {
          setShareGroupData(null);
          setShareGroupScope(null);
        }
      } catch (error) {
        console.error("Error loading sharing group:", error);
        setShareGroupData(null);
        setShareGroupScope(null);
      }
    };

    loadSharingGroup();
  }, [shareGroupToken, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name || "");
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId, user]);

  useEffect(() => {
    if (!isGuestShare || !user || !user.isAnonymous) return;
    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      setSharedRuns(Array.from(runsMap.values()));
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          runsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        upsertRuns();
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, isGuestShare, shareGroupId, user]);

  // Fetch racers
  useEffect(() => {
    if (!user || user.isAnonymous || isGuestShare) {
      setRacers([]);
      return;
    }
    
    const racersRef = collection(db, "timer_racers");
    const allRacersQuery = query(racersRef);
    const unsubscribeAll = onSnapshot(
      allRacersQuery, 
      (allSnapshot) => {
        if (allSnapshot.empty) {
          setRacers([]);
          return;
        }
        
        const allRacers = allSnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data
          };
        });
        
        // Filter by createdBy, or include if createdBy is missing
        const userRacers = allRacers.filter(racer => {
          const hasCreatedBy = racer.createdBy !== undefined && racer.createdBy !== null;
          const matchesUser = racer.createdBy === user.uid;
          
          if (matchesUser) return true;
          if (!hasCreatedBy) return true;
          return false;
        });
        
        userRacers.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRacers(userRacers);
      }, 
      (error) => {
        console.error("Error fetching racers:", error);
        setRacers([]);
      }
    );
    
    return () => {
      unsubscribeAll();
    };
  }, [isGuestShare, user]);

  useEffect(() => {
    const fetchGuestRacers = async () => {
      if (!isGuestShare || !user || !user.isAnonymous) {
        return;
      }

      const racerIds = new Set();
      sharedRuns.forEach((run) => {
        if (!Array.isArray(run.racers)) return;
        run.racers.forEach((entry) => {
          if (entry?.racerId) {
            racerIds.add(entry.racerId);
          }
        });
      });

      const uniqueIds = Array.from(racerIds);
      if (uniqueIds.length === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const chunks = chunkIds(uniqueIds);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      racerDocs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRacers(racerDocs);
    };

    fetchGuestRacers();
  }, [isGuestShare, sharedRuns, user]);

  // Fetch racers for token-based sharing
  useEffect(() => {
    const fetchTokenRacers = async () => {
      if (!isTokenShare || !tokenScope || !user) {
        return;
      }

      const accessibleRacerIds = new Set(tokenScope.racers || []);
      if (accessibleRacerIds.size === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const racerIdsArray = Array.from(accessibleRacerIds);
      const chunks = chunkIds(racerIdsArray);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      racerDocs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRacers(racerDocs);
    };

    fetchTokenRacers();
  }, [isTokenShare, tokenScope, user]);

  // Fetch racers for sharing group token
  useEffect(() => {
    const fetchShareGroupRacers = async () => {
      if (!isShareGroupShare || !shareGroupScope || !user) {
        return;
      }

      const accessibleRacerIds = new Set(shareGroupScope.racers || []);
      if (accessibleRacerIds.size === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const racerIdsArray = Array.from(accessibleRacerIds);
      const chunks = chunkIds(racerIdsArray);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      racerDocs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRacers(racerDocs);
    };

    fetchShareGroupRacers();
  }, [isShareGroupShare, shareGroupScope, user]);

  // Fetch events to calculate statistics
  useEffect(() => {
    if (isGuestShare && user?.isAnonymous) {
      setEvents(sharedRuns);
      return;
    }
    if (isTokenShare && tokenScope) {
      // For token share, we'd need to fetch runs separately
      // For now, set empty events
      setEvents([]);
      return;
    }
    if (isShareGroupShare && shareGroupScope) {
      // For sharing group share, we'd need to fetch runs separately
      // For now, set empty events
      setEvents([]);
      return;
    }

    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, "events");
        const eventsSnapshot = await getDocs(eventsRef);
        const allEvents = eventsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(allEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };
    fetchEvents();
  }, [isGuestShare, sharedRuns, user?.isAnonymous]);


  // Restore persisted filters when user changes
  useLayoutEffect(() => {
    setFiltersRestored(false);
    try {
      // Load racer group filters from shared utility
      const userId = user?.uid || null;
      const savedGroupFilters = loadRacerGroupFilters(userId);
      setSelectedGroupFilters(savedGroupFilters);

      // Load sort preferences from local storage
      const raw = localStorage.getItem(getFiltersStorageKey());
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.racerSortType === "string") {
          setRacerSortType(parsed.racerSortType);
        }
        if (typeof parsed?.sortOrder === "string") {
          setSortOrder(parsed.sortOrder);
        }
      }
    } catch (error) {
      console.error("Failed to restore racers list filters:", error);
    }
    setFiltersRestored(true);
  }, [user?.uid]);

  // Persist filters between visits
  useEffect(() => {
    if (!filtersRestored) return;
    try {
      const userId = user?.uid || null;
      // Save racer group filters using shared utility
      saveRacerGroupFilters(userId, selectedGroupFilters);

      // Save sort preferences to local storage
      const payload = {
        racerSortType,
        sortOrder,
      };
      localStorage.setItem(getFiltersStorageKey(), JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to persist racers list filters:", error);
    }
  }, [selectedGroupFilters, racerSortType, sortOrder, user?.uid, filtersRestored]);

  // Compute distinct groups
  const distinctGroups = Array.from(
    new Set(racers.flatMap((racer) => (racer.groups ? racer.groups : [])))
  ).sort();

  // Calculate statistics for each racer
  const racersWithStats = useMemo(() => {
    return racers.map((racer) => {
      let eventCount = 0;
      let totalSuccessRate = 0;
      let finishedEventCount = 0;

      events.forEach((event) => {
        if (!event.racers || !Array.isArray(event.racers)) return;

        const racerEntry = event.racers.find((r) => r.racerId === racer.id);
        if (!racerEntry) return;

        eventCount++;

        // Calculate success rate if racer finished
        if (racerEntry.duration !== null && racerEntry.duration !== undefined) {
          const finishedRacers = event.racers
            .filter((r) => r.duration !== null && r.duration !== undefined)
            .sort((a, b) => a.duration - b.duration);

          if (finishedRacers.length > 0) {
            const winningTime = finishedRacers[0].duration;
            const percentFromWinner = winningTime > 0
              ? ((racerEntry.duration - winningTime) / winningTime) * 100
              : 0;
            
            // Success rate is inverse of percent from winner (100% = same as winner, 0% = much slower)
            // We'll use rank percentage instead: (totalRacers - position + 1) / totalRacers
            const position = finishedRacers.findIndex((r) => r.racerId === racer.id) + 1;
            const rankPercent = (finishedRacers.length - position + 1) / finishedRacers.length;
            totalSuccessRate += rankPercent * 100;
            finishedEventCount++;
          }
        }
      });

      const averageSuccessRate = finishedEventCount > 0
        ? totalSuccessRate / finishedEventCount
        : 0;

      return {
        ...racer,
        eventCount,
        averageSuccessRate: Math.round(averageSuccessRate * 10) / 10, // Round to 1 decimal
      };
    });
  }, [racers, events]);

  const trackFilteredRacerIds = useMemo(() => {
    if (!normalizedTrackParam) return null;
    const ids = new Set();
    events.forEach((event) => {
      const eventTrack = (event.track || event.trackName || "").trim().toLowerCase();
      if (!eventTrack || eventTrack !== normalizedTrackParam) return;
      if (!event.racers || !Array.isArray(event.racers)) return;
      event.racers.forEach((racer) => {
        if (racer.duration !== null && racer.duration !== undefined) {
          ids.add(racer.racerId);
        }
      });
    });
    return ids;
  }, [events, normalizedTrackParam]);

  // Sorting racers
  const sortedRacers = racersWithStats.slice().sort((a, b) => {
    let comparison = 0;
    
    if (racerSortType === "name") {
      comparison = a.name.localeCompare(b.name);
    } else if (racerSortType === "created") {
      const aTime = a.createdAt ? (a.createdAt.seconds || a.createdAt) : 0;
      const bTime = b.createdAt ? (b.createdAt.seconds || b.createdAt) : 0;
      comparison = bTime - aTime;
    } else if (racerSortType === "events") {
      comparison = a.eventCount - b.eventCount;
    } else if (racerSortType === "successRate") {
      comparison = a.averageSuccessRate - b.averageSuccessRate;
    }
    
    return sortOrder === "asc" ? comparison : -comparison;
  });

  // Toggle group filter
  const toggleGroupFilter = (groupName) => {
    setSelectedGroupFilters(prev => {
      if (prev.includes(groupName)) {
        return prev.filter(g => g !== groupName);
      } else {
        return [...prev, groupName];
      }
    });
  };

  // Clear all group filters
  const clearGroupFilters = () => {
    setSelectedGroupFilters([]);
  };

  // Apply group filter and name search
  const filteredRacers = sortedRacers
    .filter((racer) => {
      if (!normalizedTrackParam) return true;
      return trackFilteredRacerIds ? trackFilteredRacerIds.has(racer.id) : false;
    })
    .filter((racer) => {
      if (selectedGroupFilters.length === 0) return true;
      return racer.groups && racer.groups.some(group => selectedGroupFilters.includes(group));
    })
    .filter((racer) =>
      racerSearchQuery === "" || racer.name.toLowerCase().includes(racerSearchQuery.toLowerCase())
    );

  const handleRenameRacer = async (racerId, newName) => {
    if (!requireAccount()) return;
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { name: newName });
      setEditingRacerId(null);
      showToast("Racer renamed successfully.");
    } catch (error) {
      console.error("Error renaming racer", error);
      showToast("Failed to rename racer.");
    }
  };

  const handleRemoveRacer = async (racerId) => {
    if (!requireAccount()) return;
    const confirmed = window.confirm(
      "Delete this racer permanently? This action cannot be undone."
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, "timer_racers", racerId));
      showToast("Racer deleted successfully.");
    } catch (error) {
      console.error("Error removing racer", error);
      showToast("Failed to delete racer. Please try again.");
    }
  };

  const handleAddRacer = async () => {
    if (!user || user.isAnonymous) {
      setShowLoginRequiredModal(true);
      return;
    }
    if (!newRacerName.trim()) {
      showToast("Please enter a racer name.");
      return;
    }
    if (!userData || userData.credits < creditCosts.CREATE_RACER) {
      showToast("Not enough credits to create a racer.");
      return;
    }
    try {
      // Deduct credits
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { credits: userData.credits - creditCosts.CREATE_RACER });
      
      const racersRef = collection(db, "timer_racers");
      await addDoc(racersRef, { 
        name: newRacerName, 
        groups: [], 
        createdBy: user.uid
      });
      showToast("Racer added successfully!");
      setNewRacerName("");
    } catch (error) {
      console.error("Error adding racer", error);
      showToast("Error adding racer, please try again.");
    }
  };

  const handleAddTag = async (racerId, tag) => {
    if (!requireAccount()) return;
    if (!tag.trim()) return;
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { groups: arrayUnion(tag) });
      setShowGroupInput(prev => ({ ...prev, [racerId]: false }));
      showToast("Group added successfully.");
    } catch (error) {
      console.error("Error adding tag", error);
      showToast("Failed to add group.");
    }
  };

  const handleRemoveTag = async (racerId, tag) => {
    if (!requireAccount()) return;
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { groups: arrayRemove(tag) });
      showToast("Group removed successfully.");
    } catch (error) {
      console.error("Error removing tag", error);
      showToast("Failed to remove group.");
    }
  };

  const handleToggleRacerSelection = (racerId) => {
    setSelectedRacers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(racerId)) {
        newSet.delete(racerId);
      } else {
        newSet.add(racerId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRacers.size === filteredRacers.length) {
      setSelectedRacers(new Set());
    } else {
      setSelectedRacers(new Set(filteredRacers.map((r) => r.id)));
    }
  };

  const handleCompare = () => {
    if (selectedRacers.size < 2) {
      showToast("Please select at least 2 racers to compare.");
      return;
    }
    const racerIds = Array.from(selectedRacers).join(",");
    navigate(`/compare/${racerIds}`);
  };

  const handleMergeRacers = () => {
    if (!requireAccount()) return;
    if (selectedRacers.size < 2) {
      showToast("Please select at least 2 racers to merge.");
      return;
    }
    
    // Get selected racers data
    const selectedRacerData = racers.filter(r => selectedRacers.has(r.id));
    
    // Combine all groups from selected racers (remove duplicates)
    const allGroups = new Set();
    selectedRacerData.forEach(racer => {
      if (racer.groups && Array.isArray(racer.groups)) {
        racer.groups.forEach(group => allGroups.add(group));
      }
    });
    
    // Pre-fill form with combined name and groups
    const combinedNames = selectedRacerData.map(r => r.name).join(" / ");
    setMergedRacerName(combinedNames);
    setMergedRacerGroups(Array.from(allGroups).join(", "));
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (!requireAccount()) return;
    if (!mergedRacerName.trim()) {
      showToast("Please enter a name for the merged racer.");
      return;
    }
    
    if (selectedRacers.size < 2) {
      showToast("Please select at least 2 racers to merge.");
      return;
    }

    try {
      const selectedRacerIds = Array.from(selectedRacers);
      const selectedRacerData = racers.filter(r => selectedRacers.has(r.id));
      
      // Combine groups (remove duplicates)
      const groupsArray = mergedRacerGroups
        .split(",")
        .map(g => g.trim())
        .filter(g => g.length > 0);
      
      // Create new merged racer (use first selected racer's createdBy or current user)
      const createdBy = selectedRacerData[0]?.createdBy || user.uid;
      const racersRef = collection(db, "timer_racers");
      const newRacerRef = await addDoc(racersRef, {
        name: mergedRacerName.trim(),
        groups: groupsArray,
        createdBy: createdBy
      });
      const newRacerId = newRacerRef.id;

      // Update all events that reference any of the old racer IDs
      const eventsRef = collection(db, "events");
      const eventsSnapshot = await getDocs(eventsRef);
      
      const updatePromises = [];
      eventsSnapshot.forEach((eventDoc) => {
        const eventData = eventDoc.data();
        if (!eventData.racers || !Array.isArray(eventData.racers)) return;
        
        // Update all racer entries that match selected racer IDs to use the new racer ID
        // This preserves ALL runs/results from all merged racers
        const updatedRacers = eventData.racers.map(racerEntry => {
          if (selectedRacerIds.includes(racerEntry.racerId)) {
            // Update this entry to use the new merged racer ID
            return {
              ...racerEntry,
              racerId: newRacerId
            };
          }
          // Keep other entries unchanged
          return racerEntry;
        });
        
        // Only update if there were any changes
        const hasChanges = updatedRacers.some((entry, index) => 
          entry.racerId !== eventData.racers[index].racerId
        );
        
        if (hasChanges) {
          updatePromises.push(
            updateDoc(doc(db, "events", eventDoc.id), {
              racers: updatedRacers
            })
          );
        }
      });

      // Wait for all event updates to complete
      await Promise.all(updatePromises);

      // Delete old racers
      const deletePromises = selectedRacerIds.map(racerId =>
        deleteDoc(doc(db, "timer_racers", racerId))
      );
      await Promise.all(deletePromises);

      // Clear selection and close modal
      setSelectedRacers(new Set());
      setShowMergeModal(false);
      setMergedRacerName("");
      setMergedRacerGroups("");
      
      showToast(`Successfully merged ${selectedRacerIds.length} racers into "${mergedRacerName.trim()}"`);
    } catch (error) {
      console.error("Error merging racers:", error);
      showToast("Failed to merge racers. Please try again.");
    }
  };

  const handleCloseMergeModal = () => {
    setShowMergeModal(false);
    setMergedRacerName("");
    setMergedRacerGroups("");
  };

  return (
    <div style={{ 
      width: "100%", 
      padding: "30px 20px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
      maxWidth: "100%"
    }}>
      {/* Main content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="homepage-other-tools" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <h1>Racers List</h1>
          
          {/* Search and Filter controls */}
          <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
            {/* Racer name search */}
            <div>
              <label htmlFor="racerSearch" style={{ marginRight: "5px" }}>Search racers:</label>
              <input
                id="racerSearch"
                type="text"
                value={racerSearchQuery}
                onChange={(e) => setRacerSearchQuery(e.target.value)}
                placeholder="Type racer name..."
                style={{ padding: "5px", width: "200px" }}
              />
            </div>
            {/* Group filter dropdown */}
            <GroupFilterDropdown
              distinctGroups={distinctGroups}
              selectedGroupFilters={selectedGroupFilters}
              onToggleGroupFilter={toggleGroupFilter}
              onClearGroupFilters={clearGroupFilters}
              label="Filter by group:"
            />
            {/* Compare Buttons Block - Top */}
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", flex: 1 }}>
              {selectedRacers.size > 0 && (
                <span style={{ fontSize: "0.9em", color: "#555" }}>
                  {selectedRacers.size} racer{selectedRacers.size !== 1 ? "s" : ""} selected
                </span>
              )}
              {isAdmin && selectedRacers.size >= 2 && (
                <button
                  onClick={handleMergeRacers}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: "#e67e22",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    borderRadius: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Merge Users ({selectedRacers.size})
                </button>
              )}
              <button
                onClick={handleCompare}
                disabled={selectedRacers.size < 2}
                style={{
                  padding: "8px 20px",
                  backgroundColor: selectedRacers.size >= 2 ? "#13A1A8" : "#ccc",
                  border: "none",
                  color: "#fff",
                  cursor: selectedRacers.size >= 2 ? "pointer" : "not-allowed",
                  borderRadius: "5px",
                  fontWeight: "bold",
                  opacity: selectedRacers.size >= 2 ? 1 : 0.6,
                }}
              >
                Compare ({selectedRacers.size})
              </button>
              {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
                <button
                  onClick={() => {
                    if (selectedRacers.size === 0) {
                      showToast("Please select at least one racer to share.");
                      return;
                    }
                    setShowShareModal(true);
                  }}
                  disabled={selectedRacers.size === 0}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: selectedRacers.size > 0 ? "#28a745" : "#ccc",
                    border: "none",
                    color: "#fff",
                    cursor: selectedRacers.size > 0 ? "pointer" : "not-allowed",
                    borderRadius: "5px",
                    fontWeight: "bold",
                    opacity: selectedRacers.size > 0 ? 1 : 0.6,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <FaShareAlt /> Share ({selectedRacers.size})
                </button>
              )}
              {selectedRacers.size > 0 && (
                <button
                  onClick={() => setSelectedRacers(new Set())}
                  style={{
                    padding: "8px 15px",
                    backgroundColor: "#999",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    borderRadius: "5px",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Select All above table */}
          <div style={{ marginBottom: "10px", display: "flex", justifyContent: "flex-end" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <input
                type="checkbox"
                checked={filteredRacers.length > 0 && selectedRacers.size === filteredRacers.length}
                onChange={handleSelectAll}
                style={{ cursor: "pointer" }}
                id="selectAllCheckbox"
              />
              <label htmlFor="selectAllCheckbox" style={{ cursor: "pointer" }}>
                Select All
              </label>
            </div>
          </div>

          {/* Racers Table */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              border: "1px solid #ccc",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #ccc" }}>
                  <th
                    style={{ textAlign: "left", cursor: "pointer", padding: "8px", backgroundColor: "#f5f5f5" }}
                    onClick={() => {
                      if (racerSortType === "name") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setRacerSortType("name");
                        setSortOrder("asc");
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      Name
                      {racerSortType === "name" && (
                        sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />
                      )}
                    </div>
                  </th>
                  <th style={{ textAlign: "left", padding: "8px", backgroundColor: "#f5f5f5" }}>Groups</th>
                  <th
                    style={{ textAlign: "center", cursor: "pointer", padding: "8px", backgroundColor: "#f5f5f5" }}
                    onClick={() => {
                      if (racerSortType === "events") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setRacerSortType("events"); // Keep as "events" for backward compatibility with data
                        setSortOrder("desc");
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                      Runs
                      {racerSortType === "events" && (
                        sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />
                      )}
                    </div>
                  </th>
                  <th
                    style={{ textAlign: "center", cursor: "pointer", padding: "8px", backgroundColor: "#f5f5f5" }}
                    onClick={() => {
                      if (racerSortType === "successRate") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setRacerSortType("successRate");
                        setSortOrder("desc");
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                      Success Rate
                      {racerSortType === "successRate" && (
                        sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />
                      )}
                    </div>
                  </th>
                  <th style={{ padding: "8px", textAlign: "center", backgroundColor: "#f5f5f5" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRacers.length > 0 ? (
                  filteredRacers.map((racer) => (
                    <tr 
                      key={racer.id} 
                      style={{ 
                        borderBottom: "1px solid #eee",
                        backgroundColor: selectedRacers.has(racer.id) ? "#f0f8ff" : "transparent"
                      }}
                    >
                      <td style={{ padding: "8px" }}>
                        {editingRacerId === racer.id ? (
                          <input
                            type="text"
                            value={renameInput}
                            onChange={(e) => setRenameInput(e.target.value)}
                            onBlur={() => handleRenameRacer(racer.id, renameInput)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleRenameRacer(racer.id, renameInput);
                              }
                            }}
                            autoFocus
                            style={{ padding: "3px", width: "100%" }}
                          />
                        ) : (
                          <span
                            onClick={() =>
                              navigate(
                                trackParam
                                  ? `/racer/${racer.id}?track=${encodeURIComponent(trackParam)}`
                                  : `/racer/${racer.id}`
                              )
                            }
                            style={{
                              cursor: "pointer",
                              color: "#13A1A8",
                              textDecoration: "underline",
                            }}
                            title="View racer details"
                          >
                            {racer.name}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "8px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                          {racer.groups && racer.groups.length > 0 ? (
                            racer.groups.map((tag, index) => (
                              <span
                                key={index}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "2px 8px",
                                  backgroundColor: "rgba(19, 161, 168, 0.125)",
                                  color: "#13A1A8",
                                  borderRadius: "12px",
                                  fontSize: "0.85em",
                                }}
                              >
                                {tag}
                                <button
                                  onClick={() => handleRemoveTag(racer.id, tag)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    color: "#13A1A8",
                                  }}
                                  title="Remove Tag"
                                >
                                  <FaTimes style={{ fontSize: "0.75em" }} />
                                </button>
                              </span>
                            ))
                          ) : (
                            <span style={{ marginRight: "5px" }}>No group</span>
                          )}
                          {showGroupInput[racer.id] ? (
                            <>
                              <input
                                type="text"
                                placeholder="New group"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleAddTag(racer.id, e.target.value);
                                  }
                                }}
                                autoFocus
                                style={{ padding: "3px", marginTop: "5px" }}
                              />
                              <button
                                onClick={() =>
                                  setShowGroupInput((prev) => ({ ...prev, [racer.id]: false }))
                                }
                                style={{
                                  background: "#13A1A8",
                                  border: "none",
                                  color: "#fff",
                                  padding: "3px 6px",
                                  borderRadius: "3px",
                                  marginLeft: "5px",
                                }}
                                title="Cancel"
                              >
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() =>
                                setShowGroupInput((prev) => ({ ...prev, [racer.id]: true }))
                              }
                              style={{
                                background: "#13A1A8",
                                border: "none",
                                color: "#fff",
                                padding: "3px 6px",
                                borderRadius: "3px",
                                marginTop: "5px",
                                fontWeight: "bold",
                              }}
                              title="Add Group"
                            >
                              +
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {racer.eventCount || 0}
                      </td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {racer.averageSuccessRate > 0 ? `${racer.averageSuccessRate}%` : "-"}
                      </td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                          {editingRacerId !== racer.id && (
                            <button
                              onClick={() => {
                                setEditingRacerId(racer.id);
                                setRenameInput(racer.name);
                              }}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#555",
                                padding: "2px 5px",
                              }}
                              title="Edit Racer"
                            >
                              <FaPencilAlt />
                            </button>
                          )}
                          {racer.createdBy === user.uid && (
                            <button
                              onClick={() => handleRemoveRacer(racer.id)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#d32f2f",
                                padding: "2px 5px",
                              }}
                              title="Delete Racer"
                            >
                              <FaTrash />
                            </button>
                          )}
                          <input
                            type="checkbox"
                            checked={selectedRacers.has(racer.id)}
                            onChange={() => handleToggleRacerSelection(racer.id)}
                            style={{ cursor: "pointer" }}
                            title="Select racer"
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "10px" }}>
                      No racers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Compare Buttons Block - Bottom */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
            {selectedRacers.size > 0 && (
              <span style={{ fontSize: "0.9em", color: "#555" }}>
                {selectedRacers.size} racer{selectedRacers.size !== 1 ? "s" : ""} selected
              </span>
            )}
            {isAdmin && selectedRacers.size >= 2 && (
              <button
                onClick={handleMergeRacers}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#e67e22",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Merge Users ({selectedRacers.size})
              </button>
            )}
            <button
              onClick={handleCompare}
              disabled={selectedRacers.size < 2}
              style={{
                padding: "8px 20px",
                backgroundColor: selectedRacers.size >= 2 ? "#13A1A8" : "#ccc",
                border: "none",
                color: "#fff",
                cursor: selectedRacers.size >= 2 ? "pointer" : "not-allowed",
                borderRadius: "5px",
                fontWeight: "bold",
                opacity: selectedRacers.size >= 2 ? 1 : 0.6,
              }}
            >
              Compare ({selectedRacers.size})
            </button>
            {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
              <button
                onClick={() => {
                  if (selectedRacers.size === 0) {
                    showToast("Please select at least one racer to share.");
                    return;
                  }
                  setShowShareModal(true);
                }}
                disabled={selectedRacers.size === 0}
                style={{
                  padding: "8px 20px",
                  backgroundColor: selectedRacers.size > 0 ? "#28a745" : "#ccc",
                  border: "none",
                  color: "#fff",
                  cursor: selectedRacers.size > 0 ? "pointer" : "not-allowed",
                  borderRadius: "5px",
                  fontWeight: "bold",
                  opacity: selectedRacers.size > 0 ? 1 : 0.6,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                <FaShareAlt /> Share ({selectedRacers.size})
              </button>
            )}
            {selectedRacers.size > 0 && (
              <button
                onClick={() => setSelectedRacers(new Set())}
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#999",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Add Racer Widget */}
          <div style={{ marginTop: "15px", display: "flex", alignItems: "center" }}>
            <input
              type="text"
              value={newRacerName}
              onChange={(e) => setNewRacerName(e.target.value)}
              placeholder="New racer name"
              style={{ padding: "5px", width: "150px", marginRight: "10px" }}
            />
            <button
              onClick={handleAddRacer}
              style={{
                padding: "5px 10px",
                backgroundColor: "#13A1A8",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                borderRadius: "3px",
              }}
            >
              Add Racer : {creditCosts.CREATE_RACER}⚡
            </button>
          </div>
        </div>
      </div>

      {/* Merge Users Modal */}
      {showMergeModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseMergeModal}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "30px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: "20px" }}>Merge Racers</h2>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              This will create a new racer and update all events to use the new racer ID. The selected racers will be deleted.
            </p>
            
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                New Racer Name:
              </label>
              <input
                type="text"
                value={mergedRacerName}
                onChange={(e) => setMergedRacerName(e.target.value)}
                placeholder="Enter merged racer name"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Groups (comma separated):
              </label>
              <input
                type="text"
                value={mergedRacerGroups}
                onChange={(e) => setMergedRacerGroups(e.target.value)}
                placeholder="Enter groups separated by commas"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={handleCloseMergeModal}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#999",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMerge}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#e67e22",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Merge Racers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Group Modal */}
      <ShareGroupModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
        }}
        resources={Array.from(selectedRacers).map(racerId => {
          const racer = filteredRacers.find(r => r.id === racerId);
          return racer ? {
            type: 'racer',
            id: racer.id,
            name: racer.name || `Racer ${racer.id}`
          } : null;
        }).filter(Boolean)}
        onShareComplete={() => {
          setShowShareModal(false);
        }}
      />

      {/* Login Required Modal */}
      {showLoginRequiredModal && (
        <div className="modal-overlay" onClick={() => setShowLoginRequiredModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0 }}>Login Required</h2>
              <button
                onClick={() => setShowLoginRequiredModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "#666",
                }}
              >
                <FaTimes />
              </button>
            </div>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              You need to be logged in to add a racer. Please create an account or sign in to continue.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowLoginRequiredModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#999",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLoginRequiredModal(false);
                  navigate("/login?signup=true");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#13A1A8",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RacersList;
